import Connections from '../../connections';
import { observable, action, computed } from 'mobx';
import { IUser, emptyUser, ChangePasswordData, ChangeMembershipIdData } from '../storeInterfaces/authInterface';
import {
  login,
  getCurrentUser,
  changePasswordRequest,
  createAdmin,
  suspendUser,
  unsuspendUser,
  changeMembershipId,
  isMainMember,
  createWalletUser,
  checkUserKYCStatus,
  user,
  getTempPassword,
} from "../../axios/requests";
import { RequestStatus } from "mobx-store";

export class AuthStore {
  @observable accessToken: string | undefined;

  @observable refreshToken: string | undefined;

  @observable logging = false;

  @observable userData: IUser = emptyUser;

  @observable isLoadingUserData = false;

  @observable isLoadingUpdateMembership = false;

  @observable passwordLoadingState = RequestStatus.IDLE;

  @observable otPassword: string = "";

  constructor() {
    const userJson = localStorage.getItem("currentUser");
    const currentUser = userJson !== null ? JSON.parse(userJson) : null;
    if (currentUser && currentUser.accessToken) {
      this.setAccessToken(currentUser.accessToken);
      this.setRefreshToken(currentUser.refreshToken);
    }
  }

  @computed
  public get isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  @action
  public setAccessToken = (token?: string) => {
    this.accessToken = token;
    localStorage.setItem(
      "currentUser",
      JSON.stringify({
        accessToken: token,
        refreshToken: this.refreshToken,
      })
    );
    this.updateDefaultHeaders(token);
  };

  @action
  public setRefreshToken = (token?: string) => {
    this.refreshToken = token;
    localStorage.setItem(
      "currentUser",
      JSON.stringify({
        accessToken: this.accessToken,
        refreshToken: token,
      })
    );
  };

  private updateDefaultHeaders(token?: string) {
    if (token) {
      Connections.wadiDeglaAxios.updateDefaultHeaders({
        Authorization: `Bearer ${token}`,
      });
    } else {
      Connections.wadiDeglaAxios.updateDefaultHeaders({
        Authorization: undefined,
      });
    }
  }

  @action
  public async login(email: string, password: string): Promise<any> {
    try {
      this.logging = true;
      const data = {
        email,
        password,
      };
      const response = await Connections.wadiDeglaAxios.httpPost(login, data);
      localStorage.setItem("currentUser", JSON.stringify(response.data));
      this.setAccessToken(response.data.accessToken);
      this.setRefreshToken(response.data.refreshToken);
      console.log(this.refreshToken);
      await this.getCurrentUser();
    } catch (error) {
      console.log(error);
    } finally {
      this.logging = false;
    }
  }
  @action
  public async createAdmin(user: any): Promise<any> {
    try {
      this.logging = true;
      user["roleId"] = 12;
      user["countryId"] = 1;
      const response = await Connections.wadiDeglaAxios.httpPost(
        createAdmin,
        user
      );
      return response.data.userId;
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    } finally {
      this.logging = false;
    }
  }

  @action.bound
  public logout(): void {
    this.setAccessToken();
    this.setRefreshToken();
    localStorage.removeItem("currentUser");
    this.userData = emptyUser;
    window.location.reload();
  }

  public async getCurrentUser(): Promise<any> {
    try {
      this.isLoadingUserData = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getCurrentUser);
      this.userData = response.data;
      this.isLoadingUserData = false;
    } catch (error) {
      this.isLoadingUserData = false;
      this.logout();
      window.location.replace("/login");
      console.log(error);
    }
  }

  public async changePassword(
    changePasswordRequestData: ChangePasswordData,
    userId: number
  ): Promise<void> {
    try {
      const requestBody = {
        newPassword: changePasswordRequestData.newPassword,
        adminPassword: changePasswordRequestData.adminPassword,
      };
      const response = await Connections.wadiDeglaAxios.httpPut(
        changePasswordRequest(userId),
        requestBody
      );
      console.log(response);
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  public async changeMembershipId(
    ChangeMembershipIdData: ChangeMembershipIdData
  ): Promise<void> {
    try {
      this.isLoadingUpdateMembership = true;
      await Connections.wadiDeglaAxios.httpPut(
        changeMembershipId,
        ChangeMembershipIdData
      );
    } catch (error) {
      throw error;
    } finally {
      this.isLoadingUpdateMembership = false;
    }
  }

  public async suspendUser(membershipId: string): Promise<void> {
    try {
      this.logging = true;
      await Connections.wadiDeglaAxios.httpDelete(suspendUser(membershipId));
    } catch (error) {
      console.log(error);
      throw Promise.reject(error);
    } finally {
      this.logging = false;
    }
  }

  public async unsuspendUser(membershipId: string): Promise<void> {
    try {
      this.logging = true;
      await Connections.wadiDeglaAxios.httpPut(unsuspendUser(membershipId), {});
    } catch (error) {
      console.log(error);
      throw Promise.reject(error);
    } finally {
      this.logging = false;
    }
  }
  public async getIsMainMember(membershipId: string): Promise<void> {
    try {
      this.logging = true;
      const res = await Connections.wadiDeglaAxios.httpGet(
        isMainMember(membershipId)
      );
      return res.data.isMainMember;
    } catch (error) {
      throw Promise.reject(error);
    } finally {
      this.logging = false;
    }
  }

  public async createWalletUser(userId: number): Promise<string> {
    try {
      this.logging = true;
      const res = await Connections.wadiDeglaAxios.httpPost(
        createWalletUser(userId),
        {}
      );
      return res.message;
    } catch (error) {
      throw error;
    } finally {
      this.logging = false;
    }
  }

  public async getUserOtPassword(userId: number): Promise<void> {
    try {
      this.passwordLoadingState = RequestStatus.LOADING;
      const res = await Connections.wadiDeglaAxios.httpGet(
        getTempPassword(userId),
        {}
      );
      this.otPassword = res.password;
      this.passwordLoadingState = RequestStatus.SUCCESS;
    } catch (error) {
      this.passwordLoadingState = RequestStatus.FAILED;
      throw error;
    }
  }

  public async deleteUser(userId: number): Promise<string> {
    try {
      this.logging = true;
      const res = await Connections.wadiDeglaAxios.httpDelete(user(userId), {});
      return res.message;
    } catch (error) {
      throw error;
    } finally {
      this.logging = false;
    }
  }
  public async softDeleteMembershipId(userId: number): Promise<string> {
    try {
      this.logging = true;
      const res = await Connections.wadiDeglaAxios.httpPut(
        `${user(userId)}/member-separation`,
        {}
      );
      return res.message;
    } catch (error) {
      throw error;
    } finally {
      this.logging = false;
    }
  }

  public async checkUserKYCStatus(membershipId: string): Promise<void> {
    try {
      this.logging = true;
      const requestBody = {
        membershipId: membershipId,
      };
      await Connections.wadiDeglaAxios.httpPost(
        checkUserKYCStatus,
        requestBody
      );
    } catch (error) {
      throw error;
    } finally {
      this.logging = false;
    }
  }

  public hasRoles(roles: string[]) {
    const userRole = this.userData.role.title;
    return roles.includes(userRole);
  }
}
